import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Eye, EyeOff, Copy, RefreshCw, Trash2, Check } from "lucide-react"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface ApiTabProps {
  hasToken: boolean
  baseUrl: string
}

export function ApiTab({ hasToken: initialHasToken, baseUrl }: ApiTabProps) {
  const [hasToken, setHasToken] = useState(initialHasToken)
  const [rawToken, setRawToken] = useState<string | null>(null)
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [generating, setGenerating] = useState(false)

  const webhookUrl = `${baseUrl}/api/intake`

  function handleCopyUrl() {
    navigator.clipboard.writeText(webhookUrl)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  async function handleGenerate() {
    if (hasToken && !confirm("This will replace your existing API token. Any integrations using the current token will stop working. Continue?")) {
      return
    }

    setGenerating(true)
    try {
      const res = await fetch("/api/api_token", {
        method: "POST",
        headers: { "X-CSRF-Token": getCSRFToken() },
      })

      if (res.ok) {
        const data = await res.json()
        setRawToken(data.token)
        setHasToken(true)
        setRevealed(true)
      }
    } finally {
      setGenerating(false)
    }
  }

  async function handleRevoke() {
    if (!confirm("Revoke your API token? Any integrations using this token will stop working.")) return

    await fetch("/api/api_token", {
      method: "DELETE",
      headers: { "X-CSRF-Token": getCSRFToken() },
    })

    setHasToken(false)
    setRawToken(null)
    setRevealed(false)
  }

  function handleCopy() {
    if (!rawToken) return
    navigator.clipboard.writeText(rawToken)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const maskedToken = rawToken
    ? rawToken.slice(0, 8) + "..." + rawToken.slice(-4)
    : "••••••••••••••••"

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold">Webhook URL</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Send a POST request to this URL to create markdown files.
        </p>
        <Separator className="my-4" />
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded bg-muted px-3 py-2 font-mono text-sm break-all">
            {webhookUrl}
          </code>
          <Button variant="outline" size="sm" onClick={handleCopyUrl}>
            {copiedUrl ? (
              <><Check className="mr-2 h-4 w-4" /> Copied</>
            ) : (
              <><Copy className="mr-2 h-4 w-4" /> Copy</>
            )}
          </Button>
        </div>

        <Separator className="my-4" />

        <div>
          <h4 className="text-sm font-medium">Request format</h4>
          <p className="mt-1 text-xs text-muted-foreground">
            Send a JSON body with an <code className="text-xs">Authorization: Bearer &lt;token&gt;</code> header.
          </p>
          <pre className="mt-3 rounded bg-muted px-4 py-3 font-mono text-xs leading-relaxed overflow-x-auto whitespace-pre">
{`{
  "title": "My note title",       // required
  "content": "The note body...",   // required
  "folder": "journal/2026",       // optional — relative path
  "date": "2026-02-13",           // optional — ISO 8601, defaults to today
  "frontmatter": {                // optional — extra front matter keys
    "tags": ["idea", "draft"],
    "source": "zapier"
  }
}`}
          </pre>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold">API Token</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Use an API token to integrate Brain Down with external tools and automations.
        </p>
        <Separator className="my-4" />

        {hasToken ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Your token</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-2 font-mono text-sm">
                  {revealed && rawToken ? rawToken : maskedToken}
                </code>
                {rawToken && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevealed(!revealed)}
                  >
                    {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  disabled={!rawToken}
                >
                  {copied ? (
                    <><Check className="mr-2 h-4 w-4" /> Copied</>
                  ) : (
                    <><Copy className="mr-2 h-4 w-4" /> Copy</>
                  )}
                </Button>
              </div>
              {rawToken ? (
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  Copy this token now. You won't be able to see it again after leaving this page.
                </p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Token is hidden. Regenerate to reveal and copy it.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {generating ? "Generating..." : "Regenerate"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleRevoke}>
                <Trash2 className="mr-2 h-4 w-4" />
                Revoke
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="mb-4 text-sm text-muted-foreground">
              No API token generated yet.
            </p>
            <Button onClick={handleGenerate} disabled={generating}>
              {generating ? "Generating..." : "Generate Token"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
